import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Phone, Mail, MapPin, ArrowRightLeft, Loader2, RefreshCw, CheckCircle2, AlertCircle,
  Send, Bell, FileText, CreditCard, Wrench, Eye, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * SMB Activation Pipeline — 9-column Kanban over the activation lifecycle.
 *
 * Read-only board (no drag-to-move — activation transitions are driven by
 * payment webhooks / form submissions / button replies, not manual
 * dragging). Each card surfaces enough context for the operator to know
 * what to chase + a single "Convert to Custom Project" action when a
 * standard package isn't enough.
 */

const STAGES = [
  { value: 'positive_response',     label: 'Positive Response',   icon: Sparkles,   accent: 'border-emerald-500', headerBg: 'bg-emerald-500/15 text-emerald-400' },
  { value: 'activation_link_sent',  label: 'Activation Link Sent', icon: Send,      accent: 'border-violet-500',  headerBg: 'bg-violet-500/15 text-violet-300' },
  { value: 'activation_paid',       label: 'Activation Paid',      icon: CreditCard, accent: 'border-sky-500',     headerBg: 'bg-sky-500/15 text-sky-300' },
  { value: 'details_submitted',     label: 'Details Submitted',    icon: FileText,  accent: 'border-cyan-500',    headerBg: 'bg-cyan-500/15 text-cyan-300' },
  { value: 'in_development',        label: 'In Development',       icon: Wrench,    accent: 'border-purple-500',  headerBg: 'bg-purple-500/15 text-purple-300' },
  { value: 'awaiting_approval',     label: 'Awaiting Approval',    icon: Eye,       accent: 'border-amber-500',   headerBg: 'bg-amber-500/15 text-amber-300' },
  { value: 'approved',              label: 'Approved',             icon: CheckCircle2, accent: 'border-teal-500', headerBg: 'bg-teal-500/15 text-teal-300' },
  { value: 'paid',                  label: 'Paid (Final)',         icon: CreditCard, accent: 'border-green-500',  headerBg: 'bg-green-500/15 text-green-300' },
  { value: 'website_live',          label: 'Website Live',         icon: CheckCircle2, accent: 'border-emerald-600', headerBg: 'bg-emerald-600/20 text-emerald-200' },
];

const STAGE_VALUES = STAGES.map(s => s.value);

const REMINDER_LABEL = { r1: 'R1', r2: 'R2', r3: 'R3', r4: 'R4' };

const RemindersBadge = ({ rem }) => {
  if (!rem) return null;
  const fired = Object.keys(REMINDER_LABEL).filter(k => rem?.[k]);
  if (!fired.length) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300"
          title={`Reminders fired: ${fired.join(', ')}`}>
      <Bell className="h-2.5 w-2.5" />{fired.map(k => REMINDER_LABEL[k]).join('·')}
    </span>
  );
};

export const SMBActivationPipeline = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [convertName, setConvertName] = useState('');
  const [convertDesc, setConvertDesc] = useState('');
  const [converting, setConverting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Pull a generous slice — the activation flow rarely exceeds a few
      // hundred concurrent leads. We bucket client-side per stage.
      const r = await api.get('/smb/leads?limit=500');
      if (r.data?.success) {
        const inFlow = (r.data.data || []).filter(l => STAGE_VALUES.includes(l.status));
        setLeads(inFlow);
      }
    } catch {
      toast.error('Failed to load activation pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = useMemo(() => {
    const buckets = STAGE_VALUES.reduce((acc, v) => { acc[v] = []; return acc; }, {});
    leads.forEach(l => { if (buckets[l.status]) buckets[l.status].push(l); });
    return buckets;
  }, [leads]);

  const openConvert = (e, lead) => {
    e.preventDefault();
    e.stopPropagation();
    setConvertLead(lead);
    setConvertName(lead.business_name || '');
    setConvertDesc(`Custom website build — converted from SMB Activation lead for ${lead.business_name || 'customer'}.`);
    setConvertOpen(true);
  };

  const submitConvert = async () => {
    if (!convertLead || converting) return;
    setConverting(true);
    try {
      const r = await api.post(`/smb/activation/${convertLead.id}/convert-to-custom-project`, {
        name: convertName.trim(),
        description: convertDesc.trim(),
      });
      if (r.data?.success) {
        if (r.data.already_converted) {
          toast.info('This lead is already converted — opening project.');
        } else {
          toast.success(`Converted to project · client: ${r.data.client?.name || 'auto-created'}`);
        }
        setConvertOpen(false);
        const pid = r.data.data?.id;
        if (pid) navigate(`/projects/${pid}`);
      }
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-activation-pipeline">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-500" />Activation Pipeline
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Leads currently in the SMB Website Activation lifecycle. Transitions are driven by payments,
            form submissions, and WhatsApp button replies — not drag.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-pipeline">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading pipeline…
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '70vh' }}>
          {STAGES.map(stage => {
            const Icon = stage.icon;
            const items = grouped[stage.value] || [];
            return (
              <div key={stage.value} className="flex-shrink-0 w-[280px]" data-testid={`pipeline-col-${stage.value}`}>
                <div className={`rounded-t-lg px-3 py-2 flex items-center justify-between ${stage.headerBg}`}>
                  <span className="font-semibold text-sm flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5" />{stage.label}
                  </span>
                  <Badge variant="secondary" className="h-5 min-w-5 flex items-center justify-center text-xs">{items.length}</Badge>
                </div>
                <div className="min-h-[200px] rounded-b-lg p-2 space-y-2 bg-muted/20 border border-border/50">
                  {items.length === 0 && (
                    <p className="text-xs text-muted-foreground/60 text-center py-4 italic">No leads in this stage</p>
                  )}
                  {items.map(lead => (
                    <Link
                      key={lead.id}
                      to={`/smb/leads/${lead.id}`}
                      className={`block rounded-lg bg-card border-t-2 ${stage.accent} border border-border p-3 cursor-pointer transition-shadow hover:shadow-md hover:border-primary/30`}
                      data-testid={`pipeline-card-${lead.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm truncate flex-1">{lead.business_name}</p>
                        {lead.paid_in_full && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-500 text-emerald-500">FULL</Badge>
                        )}
                      </div>
                      {lead.smb_project_id && (
                        <p
                          className="text-[10px] font-mono text-emerald-500/80 mt-0.5"
                          title={`Project ID: ${lead.smb_project_id}`}
                          data-testid={`pipeline-pid-${lead.id}`}
                        >
                          PID {String(lead.smb_project_id).slice(-8).toUpperCase()}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground capitalize mt-0.5">{lead.category || lead.contact_name}</p>
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                        {lead.phone && <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{lead.contact_name?.split(' ')[0] || 'Contact'}</span>}
                        {lead.email && <span className="flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" /></span>}
                        {lead.city && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{lead.city}</span>}
                      </div>
                      <div className="flex items-center justify-between gap-1.5 mt-2">
                        <div className="flex items-center gap-1 flex-wrap">
                          <RemindersBadge rem={lead.activation_reminders_sent} />
                          {(lead.revision_round || 0) > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300">
                              Rev #{lead.revision_round}
                            </span>
                          )}
                        </div>
                        {lead.smb_project_id && (
                          <button
                            type="button"
                            onClick={(e) => openConvert(e, lead)}
                            className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-0.5"
                            data-testid={`convert-btn-${lead.id}`}
                            title="Convert to Custom Project"
                          >
                            <ArrowRightLeft className="h-3 w-3" />Convert
                          </button>
                        )}
                      </div>
                      {lead.assigned_to_name && (
                        <p className="text-[10px] text-muted-foreground/70 mt-1.5 border-t border-border/40 pt-1">
                          {lead.assigned_to_name}
                        </p>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-primary" />Convert to Custom Project
            </DialogTitle>
            <DialogDescription>
              This will create a full row in the main Projects module, auto-create or link a client
              from this lead&apos;s contact details, and archive the SMB activation track for this lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Project name</Label>
              <Input value={convertName} onChange={e => setConvertName(e.target.value)}
                     data-testid="convert-name" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={convertDesc} onChange={e => setConvertDesc(e.target.value)}
                        data-testid="convert-description" />
            </div>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs p-2.5 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                After conversion this lead will be archived from the activation pipeline.
                You&apos;ll be redirected to the new project page.
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)} data-testid="convert-cancel">
              Cancel
            </Button>
            <Button onClick={submitConvert} disabled={converting} data-testid="convert-submit">
              {converting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Converting…</> : 'Convert & open project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMBActivationPipeline;
